/**
 * Routing utilities for handling dynamic base paths
 * Supports deployment to both root (/) and subdirectories (/admin/client)
 */

/**
 * Detects the base path for the application from various sources
 * 
 * Priority:
 * 1. <base> tag in HTML (can be set by server at runtime)
 * 2. Environment variable PUBLIC_URL
 * 3. Auto-detection from current path
 * 
 * @returns The base path without trailing slash (e.g., "/admin/client" or "")
 */
export function getBasePath(): string {
  // Priority 1: Check for base tag (set by server/deployment)
  const baseTag = document.querySelector('base');
  if (baseTag?.href) {
    try {
      const url = new URL(baseTag.href);
      const pathname = url.pathname.replace(/\/$/, ''); // Remove trailing slash
      console.log('[Routing] Base path from <base> tag:', pathname);
      return pathname;
    } catch (e) {
      console.warn('[Routing] Failed to parse base tag href:', baseTag.href, e);
    }
  }

  // Priority 2: Check for PUBLIC_URL environment variable
  const publicUrl = import.meta.env.PUBLIC_URL || process.env.PUBLIC_URL;
  if (publicUrl && publicUrl !== '/') {
    const normalized = publicUrl.replace(/\/$/, '');
    console.log('[Routing] Base path from PUBLIC_URL:', normalized);
    return normalized;
  }

  // Priority 3: Auto-detect from current path
  // This is a fallback that attempts to detect common deployment patterns
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  
  // Known admin portal deployment patterns
  const knownPatterns = ['admin', 'portal', 'client'];
  
  // If we're at /admin/client or similar, detect the base
  if (segments.length >= 2 && knownPatterns.includes(segments[0])) {
    const detectedBase = `/${segments[0]}/${segments[1]}`;
    console.log('[Routing] Auto-detected base path from URL:', detectedBase);
    return detectedBase;
  }
  
  if (segments.length >= 1 && knownPatterns.includes(segments[0])) {
    const detectedBase = `/${segments[0]}`;
    console.log('[Routing] Auto-detected base path from URL:', detectedBase);
    return detectedBase;
  }

  console.log('[Routing] No base path detected, using root');
  return '';
}

/**
 * Logs routing information for debugging
 */
export function logRoutingInfo(): void {
  console.log('=== Routing Configuration ===');
  console.log('Window location origin:', window.location.origin);
  console.log('Current pathname:', window.location.pathname);
  console.log('Current href:', window.location.href);
  console.log('Detected base path:', getBasePath());
  console.log('=============================');
}

